import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import { getBillActionRequests, reviewBillActionRequest } from "../storage";
import { formatDisplayDate, formatDisplayDateTime } from "../utils/dateFormat";

function actionLabel(action) {
  if (action === "edit") return "Edit";
  if (action === "delete") return "Delete";
  return String(action || "-");
}

function statusLabel(status) {
  if (status === "pending") return "Pending";
  if (status === "approved") return "Approved";
  if (status === "rejected") return "Rejected";
  if (status === "used") return "Used";
  return String(status || "-");
}

function statusColor(status) {
  if (status === "approved") return "#16a34a";
  if (status === "rejected") return "#b91c1c";
  if (status === "used") return "#0f172a";
  return "#f59e0b";
}

export default function BillRequests({ currentUser }) {
  const isAdmin = (currentUser?.role || "") === "admin";
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    setLoading(true);
    try {
      const list = await getBillActionRequests();
      setRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load bill requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
  }, [isAdmin]);

  const visible = useMemo(() => {
    if (showAll) return requests;
    return requests.filter((r) => r.status === "pending");
  }, [requests, showAll]);

  const handleReview = async (req, nextStatus) => {
    if (!req?.id) return;
    if (!window.confirm(`${nextStatus === "approved" ? "Approve" : "Reject"} this request?`)) return;
    setSaving((prev) => ({ ...prev, [req.id]: true }));
    try {
      await reviewBillActionRequest(req.id, nextStatus);
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update request.");
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        delete next[req.id];
        return next;
      });
    }
  };

  if (!isAdmin) {
    return (
      <div className="stock-inward-container" style={{ paddingTop: 0 }}>
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">Approval Requests</h2>
          </div>
          <div className="inward-form">
            <div className="empty-state" style={{ padding: "16px" }}>
              You do not have access to this page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Approval Requests</h2>
          <span className="entry-count">{visible.length} {showAll ? "requests" : "pending"}</span>
        </div>
        <div className="inward-form" style={{ paddingTop: 0 }}>
          {error ? <div className="login-error">{error}</div> : null}
          <div className="form-actions" style={{ justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem" }}>
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                Show all
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-table-container">
          <table className="entries-table">
            <thead>
              <tr>
                <th>Requested</th>
                <th>Action</th>
                <th>Bill No</th>
                <th>Date</th>
                <th>Customer</th>
                <th className="text-right">Amount</th>
                <th>Requested By</th>
                <th>Status</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-state" style={{ padding: "16px" }}>
                    No requests
                  </td>
                </tr>
              ) : (
                visible.map((req) => {
                  const bill = req.bill || {};
                  const disabled = !!saving[req.id] || req.status !== "pending";
                  return (
                    <tr key={req.id}>
                      <td>{req.requestedAt ? formatDisplayDateTime(req.requestedAt) : "-"}</td>
                      <td style={{ fontWeight: 700 }}>{actionLabel(req.action)}</td>
                      <td>{bill.billNo || "-"}</td>
                      <td>{bill.saleDate ? formatDisplayDate(bill.saleDate) : "-"}</td>
                      <td style={{ maxWidth: 260, whiteSpace: "pre-line" }}>{bill.customer || "-"}</td>
                      <td className="text-right">{bill.total != null ? Number(bill.total).toFixed(2) : "-"}</td>
                      <td>{req?.requestedBy?.username || "-"}</td>
                      <td style={{ fontWeight: 700, color: statusColor(req.status) }}>{statusLabel(req.status)}</td>
                      <td className="text-right">
                        {req.status === "pending" ? (
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                              type="button"
                              className="btn-secondary"
                              disabled={disabled}
                              onClick={() => handleReview(req, "approved")}
                              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn-remove"
                              disabled={disabled}
                              onClick={() => handleReview(req, "rejected")}
                              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: "#64748b" }}>{req.reviewedByUsername || ""}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
